import React, { useState, useEffect } from 'react';
import api from '../utils/api';

const AdminPrices = () => {
  const [priceImages, setPriceImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('courses');
  const [selectedSport, setSelectedSport] = useState('windsurfing');
  const [selectedLanguage, setSelectedLanguage] = useState('de');
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const categories = [
    { value: 'courses', label: 'Kurse (Courses)' },
    { value: 'rental', label: 'Vermietung (Rental)' }
  ];

  const sports = [
    { value: 'windsurfing', label: 'Windsurfing' },
    { value: 'kitesurfing', label: 'Kitesurfing' },
    { value: 'wingfoiling', label: 'Wingfoiling' },
    { value: 'general', label: 'Allgemein (General)' }
  ];

  const languages = [
    { value: 'de', label: '🇩🇪 Deutsch' },
    { value: 'en', label: '🇬🇧 English' }
  ];

  useEffect(() => {
    fetchPriceImages();
  }, []);

  const fetchPriceImages = async () => {
    try {
      setLoading(true);
      const response = await api.get('/price-lists/admin');
      setPriceImages(response.data);
    } catch (err) {
      console.error('Error fetching price images:', err);
      setError('Fehler beim Laden der Preislisten');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Bitte wählen Sie eine Bilddatei aus');
        return;
      }
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      setError('');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Bitte wählen Sie ein Bild aus');
      return;
    }

    try {
      setUploading(true);
      setError('');
      setSuccess('');

      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('category', selectedCategory);
      formData.append('sport', selectedSport);
      formData.append('language', selectedLanguage);

      await api.post(`/price-lists/upload?category=${selectedCategory}&sport=${selectedSport}&language=${selectedLanguage}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setSuccess('Preisliste erfolgreich hochgeladen!');
      setSelectedFile(null);
      setPreviewUrl(null);
      fetchPriceImages();
    } catch (err) {
      console.error('Error uploading image:', err);
      setError(err.response?.data?.detail || 'Fehler beim Hochladen');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm('Möchten Sie diese Preisliste wirklich löschen?')) {
      return;
    }

    try {
      await api.delete(`/price-lists/${imageId}`);
      setSuccess('Preisliste erfolgreich gelöscht!');
      fetchPriceImages();
    } catch (err) {
      console.error('Error deleting image:', err);
      setError('Fehler beim Löschen');
    }
  };

  const getCategoryLabel = (category) => {
    const cat = categories.find(c => c.value === category);
    return cat ? cat.label : category;
  };

  const getSportLabel = (sport) => {
    const sp = sports.find(s => s.value === sport);
    return sp ? sp.label : sport;
  };

  const getLanguageLabel = (lang) => {
    const l = languages.find(l => l.value === lang);
    return l ? l.label : lang;
  };

  // Group by language first, then by category-sport
  const groupedByLanguage = priceImages.reduce((acc, img) => {
    const lang = img.language || 'de';
    if (!acc[lang]) {
      acc[lang] = {};
    }
    const key = `${img.category}-${img.sport}`;
    if (!acc[lang][key]) {
      acc[lang][key] = [];
    }
    acc[lang][key].push(img);
    return acc;
  }, {});

  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold mb-6">Preislisten verwalten</h1>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {success}
        </div>
      )}

      {/* Upload Section */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Neue Preisliste hochladen</h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium mb-2">Sprache / Language</label>
            <select
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
            >
              {languages.map(lang => (
                <option key={lang.value} value={lang.value}>{lang.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Kategorie</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
            >
              {categories.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Sportart</label>
            <select
              value={selectedSport}
              onChange={(e) => setSelectedSport(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
            >
              {sports.map(sport => (
                <option key={sport.value} value={sport.value}>{sport.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-2">Bild auswählen</label>
          <input
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {previewUrl && (
          <div className="mb-4">
            <p className="text-sm font-medium mb-2">Vorschau:</p>
            <img 
              src={previewUrl} 
              alt="Preview" 
              className="max-w-full h-auto max-h-96 rounded-lg shadow-md"
            />
          </div>
        )}

        <button
          onClick={handleUpload}
          disabled={uploading || !selectedFile}
          className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          {uploading ? 'Hochladen...' : 'Hochladen'}
        </button>
      </div>

      {/* Existing Price Lists */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4">Vorhandene Preislisten</h2>

        {loading ? (
          <div className="text-center py-8">Laden...</div>
        ) : priceImages.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            Keine Preislisten vorhanden. Laden Sie die erste hoch!
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedByLanguage).map(([lang, sportGroups]) => (
              <div key={lang} className="border-2 border-gray-300 rounded-lg p-4">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                  {getLanguageLabel(lang)}
                </h3>
                <div className="space-y-4">
                  {Object.entries(sportGroups).map(([key, images]) => {
                    const [category, sport] = key.split('-');
                    return (
                      <div key={key} className="border rounded-lg p-4 bg-gray-50">
                        <h4 className="text-lg font-semibold mb-3">
                          {getSportLabel(sport)} - {getCategoryLabel(category)}
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {images.map(img => (
                            <div key={img.id} className="border rounded-lg overflow-hidden bg-white">
                              <img 
                                src={`${process.env.REACT_APP_BACKEND_URL}${img.url}`}
                                alt={img.original_filename}
                                className="w-full h-48 object-cover"
                              />
                              <div className="p-3">
                                <p className="text-sm text-gray-600 truncate mb-2">
                                  {img.original_filename}
                                </p>
                                <p className="text-xs text-gray-500 mb-2">
                                  {new Date(img.uploaded_at).toLocaleDateString('de-DE')}
                                </p>
                                <button
                                  onClick={() => handleDelete(img.id)}
                                  className="w-full bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 text-sm"
                                >
                                  Löschen
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPrices;
